import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Calendar, Clock, AlertTriangle, ChevronLeft, ChevronRight, Loader2, Lock, List, CalendarDays, Users, UserPlus, X } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TIPOS_EVENTO = {
  reunion: { label: 'Reunión', icon: '👥' },
  deadline: { label: 'Deadline', icon: '⏰' },
  recordatorio: { label: 'Recordatorio', icon: '🔔' },
  evento_institucional: { label: 'Evento Institucional', icon: '🏛️' },
  tarea: { label: 'Tarea', icon: '📋' },
  otro: { label: 'Otro', icon: '📌' }
};

const PRIORIDADES = {
  baja: { label: 'Baja', color: 'bg-green-100 text-green-800', dot: 'bg-green-500' },
  media: { label: 'Media', color: 'bg-blue-100 text-blue-800', dot: 'bg-blue-500' },
  alta: { label: 'Alta', color: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' },
  urgente: { label: 'Urgente', color: 'bg-red-100 text-red-800', dot: 'bg-red-500' }
};

const ESTADOS = {
  pendiente: { label: 'Pendiente', color: 'bg-gray-100 text-gray-800' },
  en_proceso: { label: 'En proceso', color: 'bg-blue-100 text-blue-800' },
  completado: { label: 'Completado', color: 'bg-green-100 text-green-800' }
};

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

export default function CalendarioCDPublicoPage() {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [vistaActiva, setVistaActiva] = useState('calendario'); // 'calendario' | 'lista'
  
  // Modal de evento
  const [eventoSeleccionado, setEventoSeleccionado] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [nombreParticipante, setNombreParticipante] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [mensajeModal, setMensajeModal] = useState(null);
  
  // Mes/año actual
  const hoy = new Date();
  const mesParam = parseInt(searchParams.get('mes')) || hoy.getMonth() + 1;
  const anioParam = parseInt(searchParams.get('anio')) || hoy.getFullYear();
  const [mes, setMes] = useState(mesParam);
  const [anio, setAnio] = useState(anioParam);

  useEffect(() => {
    fetchData();
  }, [token, mes, anio]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/calendario-interno/publico/${token}?mes=${mes}&anio=${anio}`);
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Link de acceso inválido o expirado');
      } else {
        setError('Error al cargar el calendario');
      }
    } finally {
      setLoading(false);
    }
  };

  const cambiarMes = (delta) => {
    let nuevoMes = mes + delta;
    let nuevoAnio = anio;
    
    if (nuevoMes > 12) {
      nuevoMes = 1;
      nuevoAnio++;
    } else if (nuevoMes < 1) {
      nuevoMes = 12;
      nuevoAnio--;
    }
    
    setMes(nuevoMes);
    setAnio(nuevoAnio);
    setSearchParams({ mes: nuevoMes, anio: nuevoAnio });
  };

  // Abrir modal con detalle del evento
  const abrirEvento = async (evento) => {
    setEventoSeleccionado(evento);
    setShowModal(true);
    setNombreParticipante('');
    setMensajeModal(null);
  };

  // Cerrar modal
  const cerrarModal = () => {
    setShowModal(false);
    setEventoSeleccionado(null);
    setNombreParticipante('');
    setMensajeModal(null);
  };

  // Anotarse en un evento
  const anotarse = async () => {
    if (!nombreParticipante.trim()) {
      setMensajeModal({ tipo: 'error', texto: 'Ingresá tu nombre' });
      return;
    }
    
    setGuardando(true);
    setMensajeModal(null);
    
    try {
      await axios.post(
        `${API_URL}/calendario-interno/publico/${token}/evento/${eventoSeleccionado.id}/participar`,
        { nombre: nombreParticipante.trim() }
      );
      
      setMensajeModal({ tipo: 'success', texto: '¡Te anotaste exitosamente!' });
      setNombreParticipante('');
      
      // Refrescar datos
      await fetchData();
      
      // Actualizar evento seleccionado
      const eventoActualizado = await axios.get(
        `${API_URL}/calendario-interno/publico/${token}/evento/${eventoSeleccionado.id}`
      );
      setEventoSeleccionado(eventoActualizado.data);
      
    } catch (err) {
      const mensaje = err.response?.data?.detail || 'Error al anotarse';
      setMensajeModal({ tipo: 'error', texto: mensaje });
    } finally {
      setGuardando(false);
    }
  };

  // Quitarse de un evento
  const quitarse = async (nombre) => {
    if (!confirm(`¿Querés quitar a ${nombre} de esta actividad?`)) return;
    
    setGuardando(true);
    setMensajeModal(null);
    
    try {
      await axios.delete(
        `${API_URL}/calendario-interno/publico/${token}/evento/${eventoSeleccionado.id}/participar/${encodeURIComponent(nombre)}`
      );
      
      setMensajeModal({ tipo: 'success', texto: `${nombre} se quitó de la actividad` });
      
      // Refrescar datos
      await fetchData();
      
      // Actualizar evento seleccionado
      const eventoActualizado = await axios.get(
        `${API_URL}/calendario-interno/publico/${token}/evento/${eventoSeleccionado.id}`
      );
      setEventoSeleccionado(eventoActualizado.data);
      
    } catch (err) {
      const mensaje = err.response?.data?.detail || 'Error al quitarse';
      setMensajeModal({ tipo: 'error', texto: mensaje });
    } finally {
      setGuardando(false);
    }
  };

  const formatearFecha = (fecha) => {
    try {
      const [year, month, day] = fecha.split('-');
      return `${day}/${month}`;
    } catch {
      return fecha;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <Lock className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Acceso Denegado</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Calendario Comisión Directiva</h1>
          <p className="text-gray-600">CATSLZ - Vista de solo lectura</p>
        </div>

        {/* Stats rápidos */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="py-3 text-center">
              <div className="text-2xl font-bold text-blue-600">{data.stats.hoy}</div>
              <div className="text-xs text-blue-700">Hoy</div>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="py-3 text-center">
              <div className="text-2xl font-bold text-purple-600">{data.stats.proximos_7_dias}</div>
              <div className="text-xs text-purple-700">Próx. 7 días</div>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="py-3 text-center">
              <div className="text-2xl font-bold text-red-600">{data.stats.urgentes_pendientes}</div>
              <div className="text-xs text-red-700">Urgentes</div>
            </CardContent>
          </Card>
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="py-3 text-center">
              <div className="text-2xl font-bold text-gray-600">{data.stats.total_mes}</div>
              <div className="text-xs text-gray-700">Este mes</div>
            </CardContent>
          </Card>
        </div>

        {/* Urgentes */}
        {data.urgentes.length > 0 && (
          <Card className="mb-6 border-red-200">
            <CardHeader className="py-3 bg-red-50">
              <CardTitle className="text-base flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-4 h-4" />
                Urgentes
              </CardTitle>
            </CardHeader>
            <CardContent className="py-3">
              <div className="space-y-2">
                {data.urgentes.map(evento => (
                  <EventoCard key={evento.id} evento={evento} showDate onClick={() => abrirEvento(evento)} />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Eventos de hoy */}
        {data.eventos_hoy.length > 0 && (
          <Card className="mb-6">
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Hoy
              </CardTitle>
            </CardHeader>
            <CardContent className="py-3">
              <div className="space-y-2">
                {data.eventos_hoy.map(evento => (
                  <EventoCard key={evento.id} evento={evento} onClick={() => abrirEvento(evento)} />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Calendario del mes */}
        <Card>
          <CardHeader className="py-3">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => cambiarMes(-1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <CardTitle className="text-base">
                {MESES[mes - 1]} {anio}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => cambiarMes(1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            {/* Tabs de vista */}
            <div className="flex justify-center gap-2 mt-3 border-t pt-3">
              <Button
                variant={vistaActiva === 'calendario' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setVistaActiva('calendario')}
              >
                <CalendarDays className="w-4 h-4 mr-1" />
                Calendario
              </Button>
              <Button
                variant={vistaActiva === 'lista' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setVistaActiva('lista')}
              >
                <List className="w-4 h-4 mr-1" />
                Lista
              </Button>
            </div>
          </CardHeader>
          <CardContent className="py-3">
            {vistaActiva === 'calendario' ? (
              /* Vista Calendario */
              (() => {
                // Calcular días del mes
                const year = anio;
                const month = mes - 1; // JavaScript months are 0-indexed
                const firstDay = new Date(year, month, 1);
                const lastDay = new Date(year, month + 1, 0);
                const daysInMonth = lastDay.getDate();
                const startDayOfWeek = firstDay.getDay(); // 0 = domingo
                
                // Crear array de días
                const days = [];
                // Días vacíos al inicio
                for (let i = 0; i < startDayOfWeek; i++) {
                  days.push(null);
                }
                // Días del mes
                for (let d = 1; d <= daysInMonth; d++) {
                  days.push(d);
                }
                
                // Obtener eventos para cada día (incluyendo eventos de varios días)
                const getEventosDelDia = (dia) => {
                  if (!dia || !data?.eventos) return [];
                  const fechaStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
                  return data.eventos.filter(e => {
                    // Evento empieza este día
                    if (e.fecha_inicio === fechaStr) return true;
                    // Evento de varios días que incluye este día
                    if (e.fecha_fin && e.fecha_inicio && e.fecha_fin > e.fecha_inicio) {
                      return fechaStr >= e.fecha_inicio && fechaStr <= e.fecha_fin;
                    }
                    return false;
                  });
                };
                
                const hoyDate = new Date();
                const esHoy = (dia) => {
                  return dia && 
                    hoyDate.getDate() === dia && 
                    hoyDate.getMonth() === month && 
                    hoyDate.getFullYear() === year;
                };
                
                const getTipoIcon = (tipo) => {
                  const t = TIPOS_EVENTO[tipo];
                  return t ? t.icon : '📌';
                };
                
                const getPrioridadDot = (prioridad) => {
                  const p = PRIORIDADES[prioridad];
                  return p ? p.dot : 'bg-gray-500';
                };

                return (
                  <div>
                    {/* Header días de la semana */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(d => (
                        <div key={d} className="text-center text-xs font-medium text-gray-500 py-1">
                          {d}
                        </div>
                      ))}
                    </div>
                    
                    {/* Grilla de días */}
                    <div className="grid grid-cols-7 gap-1">
                      {days.map((dia, idx) => {
                        const eventosDelDia = getEventosDelDia(dia);
                        return (
                          <div 
                            key={idx}
                            className={`min-h-[80px] border rounded-lg p-1 ${
                              dia ? 'bg-white' : 'bg-gray-50'
                            } ${esHoy(dia) ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
                          >
                            {dia && (
                              <>
                                <div className={`text-xs font-medium mb-1 ${esHoy(dia) ? 'text-blue-600' : 'text-gray-700'}`}>
                                  {dia}
                                </div>
                                <div className="space-y-0.5">
                                  {eventosDelDia.slice(0, 2).map(evento => (
                                    <div
                                      key={evento.id}
                                      onClick={() => abrirEvento(evento)}
                                      className={`text-xs p-0.5 rounded bg-gray-100 truncate flex items-center gap-0.5 cursor-pointer hover:bg-blue-100 transition-colors ${evento.estado === 'completado' ? 'opacity-50 line-through' : ''}`}
                                      title={`${evento.titulo} - Click para ver detalles`}
                                    >
                                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getPrioridadDot(evento.prioridad)}`}></span>
                                      {evento.participantes?.length > 0 && (
                                        <Users className="w-2.5 h-2.5 text-green-600 flex-shrink-0" />
                                      )}
                                      <span className="truncate">{evento.titulo}</span>
                                    </div>
                                  ))}
                                  {eventosDelDia.length > 2 && (
                                    <div className="text-xs text-gray-500 text-center">
                                      +{eventosDelDia.length - 2}
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()
            ) : (
              /* Vista Lista */
              data.eventos.length > 0 ? (
                <div className="space-y-2">
                  {data.eventos.map(evento => (
                    <EventoCard key={evento.id} evento={evento} showDate onClick={() => abrirEvento(evento)} />
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">No hay eventos este mes</p>
              )
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-6">
          Calendario interno - Hacé click en una actividad para ver detalles y anotarte
        </p>
      </div>

      {/* Modal de Evento */}
      <Dialog open={showModal} onOpenChange={cerrarModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-xl">{TIPOS_EVENTO[eventoSeleccionado?.tipo]?.icon || '📌'}</span>
              {eventoSeleccionado?.titulo}
            </DialogTitle>
          </DialogHeader>
          
          {eventoSeleccionado && (
            <div className="space-y-4">
              {/* Info del evento */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  {formatearFecha(eventoSeleccionado.fecha_inicio)}
                  {eventoSeleccionado.fecha_fin && eventoSeleccionado.fecha_fin !== eventoSeleccionado.fecha_inicio && (
                    <> - {formatearFecha(eventoSeleccionado.fecha_fin)}</>
                  )}
                </div>
                
                {eventoSeleccionado.hora_inicio && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="w-4 h-4" />
                    {eventoSeleccionado.hora_inicio}
                    {eventoSeleccionado.hora_fin && <> - {eventoSeleccionado.hora_fin}</>} hs
                  </div>
                )}
                
                <div className="flex gap-2 flex-wrap">
                  <Badge className={PRIORIDADES[eventoSeleccionado.prioridad]?.color || 'bg-gray-100'}>
                    {PRIORIDADES[eventoSeleccionado.prioridad]?.label || eventoSeleccionado.prioridad}
                  </Badge>
                  <Badge className={ESTADOS[eventoSeleccionado.estado]?.color || 'bg-gray-100'}>
                    {ESTADOS[eventoSeleccionado.estado]?.label || eventoSeleccionado.estado}
                  </Badge>
                </div>
                
                {eventoSeleccionado.descripcion && (
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                    {eventoSeleccionado.descripcion}
                  </p>
                )}
              </div>
              
              {/* Participantes */}
              <div className="border-t pt-4">
                <h4 className="font-medium flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4" />
                  Participantes ({eventoSeleccionado.participantes?.length || 0})
                </h4>
                
                {eventoSeleccionado.participantes?.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {eventoSeleccionado.participantes.map((p, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-green-50 px-3 py-2 rounded-lg">
                        <span className="font-medium text-green-800">{p.nombre}</span>
                        <button
                          onClick={() => quitarse(p.nombre)}
                          className="text-red-500 hover:text-red-700 p-1"
                          title="Quitarse"
                          disabled={guardando}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 mb-4">
                    Todavía no hay nadie anotado. ¡Sé el primero!
                  </p>
                )}
                
                {/* Formulario para anotarse */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Tu nombre"
                    value={nombreParticipante}
                    onChange={(e) => setNombreParticipante(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && anotarse()}
                    disabled={guardando}
                    className="flex-1"
                  />
                  <Button 
                    onClick={anotarse} 
                    disabled={guardando || !nombreParticipante.trim()}
                    className="gap-1"
                  >
                    {guardando ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <UserPlus className="w-4 h-4" />
                    )}
                    Anotarme
                  </Button>
                </div>
                
                {/* Mensaje */}
                {mensajeModal && (
                  <div className={`mt-3 p-2 rounded text-sm ${
                    mensajeModal.tipo === 'success' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {mensajeModal.texto}
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={cerrarModal}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Componente de evento
function EventoCard({ evento, showDate = false, onClick }) {
  const tipo = TIPOS_EVENTO[evento.tipo] || TIPOS_EVENTO.otro;
  const prioridad = PRIORIDADES[evento.prioridad] || PRIORIDADES.media;
  const estado = ESTADOS[evento.estado] || ESTADOS.pendiente;
  const participantesCount = evento.participantes?.length || 0;

  const formatearFecha = (fecha) => {
    try {
      const [year, month, day] = fecha.split('-');
      return `${day}/${month}`;
    } catch {
      return fecha;
    }
  };

  return (
    <div 
      className={`p-3 rounded-lg border bg-white cursor-pointer hover:shadow-md hover:border-blue-300 transition-all ${evento.estado === 'completado' ? 'opacity-60' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <span className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${prioridad.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-lg">{tipo.icon}</span>
            <span className={`font-medium ${evento.estado === 'completado' ? 'line-through text-gray-500' : ''}`}>
              {evento.titulo}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1 text-sm text-gray-500 flex-wrap">
            {showDate && (
              <span>
                {formatearFecha(evento.fecha_inicio)}
                {evento.fecha_fin && evento.fecha_fin !== evento.fecha_inicio && (
                  <> - {formatearFecha(evento.fecha_fin)}</>
                )}
              </span>
            )}
            {evento.hora_inicio && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {evento.hora_inicio}
                {evento.hora_fin && <>-{evento.hora_fin}</>}
                hs
              </span>
            )}
            {participantesCount > 0 && (
              <span className="flex items-center gap-1 text-green-600">
                <Users className="w-3 h-3" />
                {participantesCount}
              </span>
            )}
            {evento.estado !== 'pendiente' && (
              <Badge className={estado.color + " text-xs"}>{estado.label}</Badge>
            )}
            <Badge className={prioridad.color + " text-xs"}>{prioridad.label}</Badge>
          </div>
          {evento.descripcion && (
            <p className="text-sm text-gray-600 mt-1 line-clamp-2">{evento.descripcion}</p>
          )}
        </div>
      </div>
    </div>
  );
}
